import { getFragmentData } from '@packages/gql/generated/shopping';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { ProductRecommendationFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductRecommendationFragmentFragmentDoc';
import { PrudsysCategoryToProductsPapiDocument } from '@packages/gql/generated/shopping/PrudsysCategoryToProductsPapiDocument';
import { getPapidata } from '../../../helpers/getPapiData';
import { useRecoElement } from '../../../helpers/useRecoElement';
import type { ProductsRecoSliderProps, RdeType, RequestParams } from '../../../types';
import { RecoCarousel } from '../../RecoCarousel';
import { RecoProductCard } from '../../RecoProductCard';
import { useProductsRecoTracking } from '../../RecoProductCard/useProductsRecoTracking';
import { RecoProductCardSkeleton } from '../../RecoProductCardSkeleton';

export type PrudsysCategoryToProductsPapiRecoSliderProps = Pick<RequestParams, 'categoryId'> &
  ProductsRecoSliderProps;

/* GraphQL */ `
  query PrudsysCategoryToProductsPapi(
    $locale: String!
    $categoryId: String!
    $papiproducts: [String!]!
  ) {
    prudsysCategoryToProductsPapi(
      locale: $locale
      categoryId: $categoryId
      papiproducts: $papiproducts
    ) {
      title
      type
      recommendations {
        ...ProductRecommendationFragment
      }
      extendedViewUrl
    }
  }
`;

const RDE_TYPE: RdeType = 'prudsysCategoryToProductsPapi';

/**
 * PrudsysCategoryToProductsProductsRecoSlider component description displayed in storybook
 * */
export const PrudsysCategoryToProductsPapiRecoSlider: FC<
  PrudsysCategoryToProductsPapiRecoSliderProps
> = ({
  categoryId,
  elementIndex = 0,
  fetchOnThreshold = false,
  pageTemplate,
  preferTitle = false,
  title,
}) => {
  const { locale } = useIntl();
  const { isInThreshold, isInView, recoEl } = useRecoElement();

  const [{ data, error }] = useQuery({
    query: PrudsysCategoryToProductsPapiDocument,
    variables: {
      categoryId,
      papiproducts: getPapidata('hotArticlesIntraday', []),
      locale: locale!,
    },
    context: useMemo(() => ({ suspense: false }), []),
    pause: fetchOnThreshold ? !isInThreshold : false,
    requestPolicy: 'network-only',
  });

  const recommendations = data ? data.prudsysCategoryToProductsPapi.recommendations : undefined;

  /** Filtered out recommendations that don't have a product (product === null) */
  const filteredRecommendations = recommendations
    ? recommendations.filter(
        (recommendation) =>
          getFragmentData(ProductRecommendationFragmentFragmentDoc, recommendation).product,
      )
    : undefined;

  const rdeTypeAbbr = data?.prudsysCategoryToProductsPapi.type;

  useProductsRecoTracking({
    elementIndex,
    pageTemplate,
    rdeType: RDE_TYPE,
    rdeTypeAbbr,
    recommendations: filteredRecommendations,
    title,
    trackingCondition: isInView && !!filteredRecommendations && filteredRecommendations.length > 0,
  });

  if (
    (error && !filteredRecommendations) ||
    (filteredRecommendations && filteredRecommendations.length === 0)
  ) {
    return null;
  }

  return (
    <RecoCarousel
      title={preferTitle ? title : data?.prudsysCategoryToProductsPapi.title || title}
      ref={recoEl}
      loadingState={!filteredRecommendations}
    >
      {!filteredRecommendations
        ? // rule is disabled because order doesn't change between rerenders
          // eslint-disable-next-line react/no-array-index-key
          [...Array(3)].map((_, i) => <RecoProductCardSkeleton key={i} />)
        : filteredRecommendations.map((recommendation, idx) => (
            <RecoProductCard
              pageTemplate={pageTemplate}
              // rule is disabled because order doesn't change between rerenders
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              elementIndex={elementIndex}
              position={idx}
              recommendationData={recommendation}
              productsLength={filteredRecommendations.length}
              rdeType={RDE_TYPE}
            />
          ))}
    </RecoCarousel>
  );
};
